import { useMutation } from '@apollo/client';
import type { DeleteInternalAuditReportsMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { DeleteInternalAuditReportsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useDeleteInternalAuditReportsTRPC } from './useDeleteInternalAuditReportsTRPC';

export const useDeleteInternalAuditReports = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [deleteInternalAuditReportsGraphQL, graphqlState] = useMutation(
    DeleteInternalAuditReportsDocument,
    {
      update: (cache) => {
        evictField(cache, 'internal_audit_report');
        evictField(cache, 'internal_audit_report_aggregate');
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useDeleteInternalAuditReportsTRPC();

  const deleteInternalAuditReports = async (
    ids: string[]
  ): Promise<DeleteInternalAuditReportsMutation> => {
    if (trpcEnabled) {
      return trpcMutation.deleteInternalAuditReports(ids);
    }

    const result = await deleteInternalAuditReportsGraphQL({
      variables: { Ids: ids },
    });
    if (!result.data) {
      throw new Error('Failed to delete internal audit reports');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      deleteInternalAuditReports,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    deleteInternalAuditReports,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
